import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { Seo } from "../components/Seo";
import { GoogleButton } from "../components/GoogleAuth";

const inputCls =
    "w-full rounded-2xl border border-ink/10 bg-white px-5 py-3.5 text-base text-ink placeholder:text-smoke/60 transition-colors focus:border-saffron focus:outline-none";

const SignupPage = () => {
    const { user, signup } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from || "/";

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    if (user) return <Navigate to={from} replace />;

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        const res = await signup(name.trim(), email.trim(), password);
        setBusy(false);
        if (res.ok) navigate(from, { replace: true });
        else setError(res.error);
    };

    return (
        <>
            <Seo title="Sign up — SahiKhoj" description="Create your free SahiKhoj account to save offers and unlock more features." path="/signup" noindex />
            <section className="flex min-h-[80vh] items-center bg-cream px-6 pb-20 pt-32 sm:px-8">
                <div data-testid="signup-page" className="mx-auto w-full max-w-md rounded-3xl border border-ink/5 bg-white p-8 shadow-sm sm:p-10">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-ink">
                        <span className="h-4 w-4 rounded-full border-[2.5px] border-saffron" />
                    </span>
                    <h1 className="mt-6 font-display text-3xl font-semibold tracking-tight text-ink">Create your account</h1>
                    <p className="mt-2 text-sm text-smoke">Free forever. Save offers, compare prices and more.</p>
                    <div className="mt-7">
                        <GoogleButton label="Sign up with Google" testId="signup-google-button" />
                    </div>
                    <div className="mt-5 flex items-center gap-3">
                        <span className="h-px flex-1 bg-ink/10" />
                        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-smoke/70">or with email</span>
                        <span className="h-px flex-1 bg-ink/10" />
                    </div>
                    <form onSubmit={submit} className="mt-5 space-y-4" data-testid="signup-form">
                        <input type="text" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className={inputCls} data-testid="signup-name-input" autoComplete="name" />
                        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address" className={inputCls} data-testid="signup-email-input" autoComplete="email" />
                        <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password (min. 6 characters)" className={inputCls} data-testid="signup-password-input" autoComplete="new-password" />
                        {error && <p data-testid="signup-error" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}
                        <button type="submit" disabled={busy} data-testid="signup-submit-button" className="w-full rounded-full bg-saffron px-8 py-4 text-base font-medium text-white transition-colors hover:bg-saffron-hover disabled:opacity-60">
                            {busy ? "Creating account…" : "Sign up free"}
                        </button>
                    </form>
                    <p className="mt-5 text-sm text-smoke">
                        Already have an account?{" "}
                        <Link to="/login" data-testid="go-login-link" className="font-medium text-india transition-colors hover:text-saffron">
                            Log in
                        </Link>
                    </p>
                </div>
            </section>
        </>
    );
};

export default SignupPage;
